import { useEffect, useMemo, useRef, useState } from "react";
import { auth } from "../../firebase";
import useUserProfileStore, {
  selectUserSubscriptionData,
  selectUserSubscriptionTier,
} from "../../store/useUserProfileStore";
import {
  fetchNearbyTimeCapsules,
  fetchTimeCapsuleById,
  fetchUserFoundAskTimeCapsules,
  fetchUserDraftTimeCapsules,
  fetchUserSentTimeCapsules,
} from "../../services/timeCapsuleService";
import { getSponsoredBusinessMarkerMap } from "../../services/sponsoredAvatarService";
import { hasInsiderAccess } from "../../utils/insiderAccess";
import { isCapsuleEnded } from "../../utils/capsuleAvailability";
import buildHomeMapDisplayItemsV2 from "./buildHomeMapDisplayItemsV2";
import {
  HOME_MAP_DEFAULT_FETCH_RADIUS_METERS,
  HOME_MAP_MAX_FETCH_RADIUS_METERS,
  buildHomeMapFetchRadiusMeters,
  buildHomeMapMarkerFromCapsule,
  buildHomeMapMarkerFromDraft,
  buildHomeMapMarkerFromSentCapsule,
  buildHomeMapViewportCacheKey,
  distanceMetersBetween,
  filterVisibleHomeMapMarkers,
  isLatLng,
  mergeHomeMapMarkers,
  normalizeCapsuleType,
} from "./homeMapV2Utils";

const CACHE_TTL_MS = 60000;

// A capsule the viewer has archived or deleted should never render on the map
// (as a marker, in a cluster, or in a list).
function isCapsuleHiddenForUser(capsule, userId = "") {
  if (!userId) return false;
  const archivedBy = Array.isArray(capsule?.archivedBy) ? capsule.archivedBy : [];
  const deletedBy = Array.isArray(capsule?.deletedBy) ? capsule.deletedBy : [];
  return archivedBy.includes(userId) || deletedBy.includes(userId);
}

function filterFetchedCapsules(capsules = [], userId = "") {
  const now = Date.now();
  return (Array.isArray(capsules) ? capsules : []).filter((capsule) => {
    if (isCapsuleHiddenForUser(capsule, userId)) {
      return false;
    }

    const normalizedType = normalizeCapsuleType(capsule?.capsuleType);
    if (normalizedType === "private") {
      const recipients = Array.isArray(capsule?.recipientUserIds) ? capsule.recipientUserIds : [];
      if (!userId || !recipients.includes(userId)) {
        return false;
      }
    }

    return !isCapsuleEnded(capsule, now);
  });
}

// Only sponsored markers depend on the business document for their *icon*, so
// only they wait for it. Member exclusive markers render immediately — their
// chest never changes — and pick up their location label when metadata lands.
function hasSponsoredBusinessMarkerMetadata(sponsoredBusinessAvatarMap = {}, capsule = {}) {
  if (normalizeCapsuleType(capsule?.capsuleType) !== "sponsored") return true;
  if (normalizeCapsuleType(capsule?.capsuleType) !== "sponsored") return true;
  const businessId = String(capsule?.businessId || capsule?.senderId || "").trim();
  if (!businessId) return true;
  return Object.prototype.hasOwnProperty.call(sponsoredBusinessAvatarMap || {}, businessId);
}

function findReusableNearbyCacheEntry(cacheMap, queryCenter, queryRadius) {
  if (!cacheMap?.size || !isLatLng(queryCenter)) return null;

  for (const entry of cacheMap.values()) {
    const entryCenter = entry?.center;
    const entryRadius = Number(entry?.radius);
    const entryCapsules = Array.isArray(entry?.capsules) ? entry.capsules : [];
    const entryAgeMs = Date.now() - Number(entry?.at || 0);
    if (!isLatLng(entryCenter) || !Number.isFinite(entryRadius) || !entryCapsules.length) {
      continue;
    }

    const reuseDistanceMeters = Math.max(
      250,
      entryRadius
    );
    const centerDistanceMeters = distanceMetersBetween(entryCenter, queryCenter);
    if (Number.isFinite(centerDistanceMeters) && centerDistanceMeters <= reuseDistanceMeters) {
      return {
        ...entry,
        ageMs: entryAgeMs,
      };
    }
  }

  return null;
}

export default function useHomeMapCapsulesV2({
  center,
  viewport,
  refreshKey = 0,
  usersList = [],
  visibility = {},
  hiddenMarkerIds = new Set(),
  optimisticCapsules = [],
  optimisticPublicCapsules = [],
  focusedCapsuleId = "",
  userId = "",
}) {
  const subscriptionData = useUserProfileStore(selectUserSubscriptionData);
  const subscriptionTier = useUserProfileStore(selectUserSubscriptionTier);
  const [nearbyCapsules, setNearbyCapsules] = useState([]);
  const [draftCapsules, setDraftCapsules] = useState([]);
  const [foundAskCapsules, setFoundAskCapsules] = useState([]);
  const [sentCapsules, setSentCapsules] = useState([]);
  const [focusedCapsule, setFocusedCapsule] = useState(null);
  const [sponsoredBusinessAvatarMap, setSponsoredBusinessAvatarMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const requestIdRef = useRef(0);
  const nearbyCacheRef = useRef(new Map());
  const userCapsuleCacheRef = useRef(new Map());

  const uid = useMemo(
    () => String(userId || auth.currentUser?.uid || "").trim(),
    [userId]
  );
  const normalizedFocusedCapsuleId = useMemo(
    () => String(focusedCapsuleId || "").trim(),
    [focusedCapsuleId]
  );
  const centerLat = Number(center?.lat);
  const centerLng = Number(center?.lng);
  const viewportCenterLat = Number(viewport?.center?.lat);
  const viewportCenterLng = Number(viewport?.center?.lng);
  const viewportNorth = Number(viewport?.bounds?.north);
  const viewportEast = Number(viewport?.bounds?.east);
  const viewportSouth = Number(viewport?.bounds?.south);
  const viewportWest = Number(viewport?.bounds?.west);
  const viewportZoom = Number(viewport?.zoom);
  const fetchQuery = useMemo(() => {
    const normalizedViewport = {
      zoom: Number.isFinite(viewportZoom) ? viewportZoom : undefined,
      center:
        Number.isFinite(viewportCenterLat) && Number.isFinite(viewportCenterLng)
          ? { lat: viewportCenterLat, lng: viewportCenterLng }
          : null,
      bounds:
        Number.isFinite(viewportNorth) &&
        Number.isFinite(viewportEast) &&
        Number.isFinite(viewportSouth) &&
        Number.isFinite(viewportWest)
          ? {
              north: viewportNorth,
              east: viewportEast,
              south: viewportSouth,
              west: viewportWest,
            }
          : null,
    };
    const fallbackCenter =
      Number.isFinite(centerLat) && Number.isFinite(centerLng)
        ? { lat: centerLat, lng: centerLng }
        : null;
    const queryCenter = isLatLng(normalizedViewport.center)
      ? normalizedViewport.center
      : fallbackCenter;
    if (!isLatLng(queryCenter)) return null;

    const queryRadius = buildHomeMapFetchRadiusMeters(
      normalizedViewport,
      HOME_MAP_DEFAULT_FETCH_RADIUS_METERS,
      HOME_MAP_MAX_FETCH_RADIUS_METERS
    );

    return {
      center: queryCenter,
      radius: queryRadius,
      cacheKey: `${buildHomeMapViewportCacheKey(
        normalizedViewport.center ? normalizedViewport : { center: queryCenter },
        queryRadius
      )}::refresh:${refreshKey}`,
    };
  }, [
    centerLat,
    centerLng,
    refreshKey,
    viewportCenterLat,
    viewportCenterLng,
    viewportEast,
    viewportNorth,
    viewportSouth,
    viewportWest,
    viewportZoom,
  ]);

  useEffect(() => {
    if (!fetchQuery) {
      setNearbyCapsules([]);
      setSponsoredBusinessAvatarMap({});
      setLoading(false);
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    let cancelled = false;
    const { center: queryCenter, radius: queryRadius, cacheKey } = fetchQuery;

    const isStale = () => cancelled || requestId !== requestIdRef.current;

    const run = async () => {
      setError("");
      try {
        const cacheEntry =
          (cacheKey ? nearbyCacheRef.current.get(cacheKey) : null) ||
          findReusableNearbyCacheEntry(nearbyCacheRef.current, queryCenter, queryRadius);
        const cachedCapsules = Array.isArray(cacheEntry?.capsules) ? cacheEntry.capsules : [];
        const cacheAgeMs = cacheEntry
          ? Number(cacheEntry.ageMs ?? Date.now() - Number(cacheEntry.at || 0))
          : Number.POSITIVE_INFINITY;
        const hasReusableCache = cachedCapsules.length > 0;

        if (hasReusableCache) {
          setNearbyCapsules(cachedCapsules);
        }

        if (cacheEntry && cacheAgeMs <= CACHE_TTL_MS) {
          setLoading(false);
          return;
        }

        setLoading(!hasReusableCache);
        const fetchedCapsules = await fetchNearbyTimeCapsules({
          center: queryCenter,
          radius: queryRadius,
        });
        if (isStale()) return;

        const normalizedFetchedCapsules = Array.isArray(fetchedCapsules) ? fetchedCapsules : [];
        if (cacheKey) {
          nearbyCacheRef.current.set(cacheKey, {
            at: Date.now(),
            center: queryCenter,
            capsules: normalizedFetchedCapsules,
            radius: queryRadius,
          });
        }
        setNearbyCapsules(normalizedFetchedCapsules);
      } catch (err) {
        console.error("Failed to load home map capsules:", err);
        if (!isStale()) {
          setError("Failed to load map capsules.");
        }
      } finally {
        if (!isStale()) {
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [fetchQuery]);

  useEffect(() => {
    let cancelled = false;

    if (!uid) {
      setDraftCapsules([]);
      setFoundAskCapsules([]);
      setSentCapsules([]);
      return undefined;
    }

    const cacheKey = `${uid}::refresh:${refreshKey}`;
    const cacheEntry = userCapsuleCacheRef.current.get(cacheKey) || null;
    const cachedDrafts = Array.isArray(cacheEntry?.drafts) ? cacheEntry.drafts : [];
    const cachedSentCapsules = Array.isArray(cacheEntry?.sentCapsules)
      ? cacheEntry.sentCapsules
      : [];
    const cachedFoundAskCapsules = Array.isArray(cacheEntry?.foundAskCapsules)
      ? cacheEntry.foundAskCapsules
      : [];
    const cacheAgeMs = cacheEntry
      ? Date.now() - Number(cacheEntry.at || 0)
      : Number.POSITIVE_INFINITY;

    if (cachedDrafts.length || cachedSentCapsules.length || cachedFoundAskCapsules.length) {
      setDraftCapsules(cachedDrafts);
      setFoundAskCapsules(cachedFoundAskCapsules);
      setSentCapsules(cachedSentCapsules);
    }

    if (cacheEntry && cacheAgeMs <= CACHE_TTL_MS) {
      return undefined;
    }

    const run = async () => {
      try {
        const [nextDrafts, nextSentCapsules, nextFoundAskCapsules] = await Promise.all([
          fetchUserDraftTimeCapsules(uid),
          fetchUserSentTimeCapsules(uid),
          fetchUserFoundAskTimeCapsules(uid),
        ]);
        if (cancelled) return;

        const drafts = Array.isArray(nextDrafts) ? nextDrafts : [];
        const sent = Array.isArray(nextSentCapsules) ? nextSentCapsules : [];
        const foundAsk = Array.isArray(nextFoundAskCapsules) ? nextFoundAskCapsules : [];
        userCapsuleCacheRef.current.set(cacheKey, {
          at: Date.now(),
          drafts,
          foundAskCapsules: foundAsk,
          sentCapsules: sent,
        });
        setDraftCapsules(drafts);
        setFoundAskCapsules(foundAsk);
        setSentCapsules(sent);
      } catch (err) {
        console.error("Failed to load user home map capsules:", err);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [refreshKey, uid]);

  const filteredFetchedCapsules = useMemo(
    () => filterFetchedCapsules(nearbyCapsules, uid),
    [nearbyCapsules, uid]
  );
  const sponsoredBusinessIds = useMemo(
    () =>
      Array.from(
        new Set(
          filteredFetchedCapsules
            .filter((capsule) => {
              const type = normalizeCapsuleType(capsule?.capsuleType);
              return type === "sponsored" || type === "memberexclusive";
            })
            .map((capsule) => String(capsule?.businessId || capsule?.senderId || "").trim())
            .filter(Boolean)
        )
      ),
    [filteredFetchedCapsules]
  );

  useEffect(() => {
    let cancelled = false;

    if (!sponsoredBusinessIds.length) {
      setSponsoredBusinessAvatarMap({});
      return undefined;
    }

    const run = async () => {
      try {
        const nextSponsoredBusinessAvatarMap = await getSponsoredBusinessMarkerMap(
          sponsoredBusinessIds
        );
        if (!cancelled) {
          setSponsoredBusinessAvatarMap(nextSponsoredBusinessAvatarMap || {});
        }
      } catch (err) {
        console.error("Failed to load sponsored business pins:", err);
        if (!cancelled) {
          setSponsoredBusinessAvatarMap(
            sponsoredBusinessIds.reduce((map, businessId) => {
              map[businessId] = { avatarUrl: "", companyName: "", locations: [] };
              return map;
            }, {})
          );
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [sponsoredBusinessIds]);

  useEffect(() => {
    let cancelled = false;
    if (!normalizedFocusedCapsuleId) {
      setFocusedCapsule(null);
      return undefined;
    }

    const run = async () => {
      try {
        const capsule = await fetchTimeCapsuleById(normalizedFocusedCapsuleId);
        if (!cancelled) setFocusedCapsule(capsule || null);
      } catch (err) {
        console.warn("Failed to load focused map capsule:", err);
        if (!cancelled) setFocusedCapsule(null);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [normalizedFocusedCapsuleId]);

  const allMarkers = useMemo(() => {
    const isSubscribedUser = hasInsiderAccess(subscriptionData, subscriptionTier);

    const normalizedFetchedMarkers = filteredFetchedCapsules
      .filter((capsule) => hasSponsoredBusinessMarkerMetadata(sponsoredBusinessAvatarMap, capsule))
      .map((capsule) =>
        buildHomeMapMarkerFromCapsule(capsule, {
          userId: uid,
          isSubscribedUser,
          sponsoredBusinessAvatarMap,
          usersList: Array.isArray(usersList) ? usersList : [],
        })
      )
      .filter(Boolean);
    const normalizedDraftMarkers = (Array.isArray(draftCapsules) ? draftCapsules : [])
      .map((draft) => buildHomeMapMarkerFromDraft(draft))
      .filter(Boolean);
    const normalizedSentMarkers = (Array.isArray(sentCapsules) ? sentCapsules : [])
      .filter((capsule) => !isCapsuleHiddenForUser(capsule, uid))
      .map((capsule) => buildHomeMapMarkerFromSentCapsule(capsule, uid))
      .filter(Boolean);
    const normalizedOptimisticMarkers = (
      Array.isArray(optimisticCapsules) ? optimisticCapsules : []
    )
      .map((capsule) => buildHomeMapMarkerFromSentCapsule(capsule, uid))
      .filter(Boolean);
    const normalizedOptimisticPublicMarkers = (
      Array.isArray(optimisticPublicCapsules) ? optimisticPublicCapsules : []
    )
      .filter((capsule) => hasSponsoredBusinessMarkerMetadata(sponsoredBusinessAvatarMap, capsule))
      .map((capsule) =>
        buildHomeMapMarkerFromCapsule(capsule, {
          userId: uid,
          isSubscribedUser,
          sponsoredBusinessAvatarMap,
          usersList: Array.isArray(usersList) ? usersList : [],
          includeDiscoverOnly: true,
        })
      )
      .filter(Boolean);
    // Found-Ask and focused capsules are merged in after filterFetchedCapsules
    // has already run, so they need their own end-date gate — otherwise a
    // deep link (?focusedCapsuleId=...) or a previously found capsule puts an
    // expired sponsored/memberExclusive pin back on the map.
    const normalizedFoundAskMarkers = (Array.isArray(foundAskCapsules) ? foundAskCapsules : [])
      .filter((capsule) => !isCapsuleHiddenForUser(capsule, uid))
      .filter((capsule) => !isCapsuleEnded(capsule))
      .filter((capsule) => hasSponsoredBusinessMarkerMetadata(sponsoredBusinessAvatarMap, capsule))
      .map((capsule) =>
        buildHomeMapMarkerFromCapsule(capsule, {
          userId: uid,
          isSubscribedUser,
          sponsoredBusinessAvatarMap,
          usersList: Array.isArray(usersList) ? usersList : [],
          includeDiscoverOnly: true,
        })
      )
      .filter(Boolean);
    const focusedMarker = focusedCapsule
      && !isCapsuleEnded(focusedCapsule)
      && hasSponsoredBusinessMarkerMetadata(sponsoredBusinessAvatarMap, focusedCapsule)
      ? buildHomeMapMarkerFromCapsule(focusedCapsule, {
          userId: uid,
          isSubscribedUser,
          sponsoredBusinessAvatarMap,
          usersList: Array.isArray(usersList) ? usersList : [],
          includeDiscoverOnly: true,
        })
      : null;

    return mergeHomeMapMarkers(
      focusedMarker ? [focusedMarker] : [],
      normalizedOptimisticMarkers,
      normalizedOptimisticPublicMarkers,
      normalizedFoundAskMarkers,
      normalizedFetchedMarkers,
      normalizedDraftMarkers,
      normalizedSentMarkers
    );
  }, [
    draftCapsules,
    focusedCapsule,
    filteredFetchedCapsules,
    foundAskCapsules,
    optimisticCapsules,
    optimisticPublicCapsules,
    sentCapsules,
    sponsoredBusinessAvatarMap,
    subscriptionData,
    subscriptionTier,
    uid,
    usersList,
  ]);

  const markers = useMemo(
    () =>
      filterVisibleHomeMapMarkers(allMarkers, {
        hiddenMarkerIds,
        visibility,
        userId,
      }),
    [allMarkers, hiddenMarkerIds, visibility, userId]
  );

  const displayItems = useMemo(
    () => buildHomeMapDisplayItemsV2(markers, Number(viewport?.zoom) || 18),
    [markers, viewport?.zoom]
  );

  return {
    markers,
    displayItems,
    loading,
    error,
  };
}
